import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Tooltip, message } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import './index.less';
import { cn } from '../../utils/ClassNamesUtils';
import useArrowKeyNavigation from '../../hooks/useArrowKeyNavigation';

const recordstoNumberedMap = (map, state) => {
	const newMap = {};
	const pagination = state.pagination;
	const startingIndex = pagination.pageSize * (pagination.current - 1);
	const ids = state.recordsIds;

	ids.map((element, index) => {
		newMap[startingIndex + index] = element;
	});

	return { ...map, ...newMap };
};

function NextPreviousButton({
	activeId,
	getEnitityUrl,
	stateSlice,
	targetCollectionName,
	location,
	history,
	updatePagination,
	cloneTargetCollection,
	entityName,
	wrapperclassName,
}) {
	const targetCollection = 'FETCH_DATA_FROM_SERVER_widget_tempNextPrevCollection';

	const [loading, setLoading] = React.useState(false);
	const [idMap, setIdMap] = React.useState(
		recordstoNumberedMap({}, stateSlice[targetCollectionName])
	);
	const indexInCurrentSlice = stateSlice[targetCollectionName].recordsIds.findIndex(
		(item) => String(item) === String(activeId)
	);
	const total = nullSafeGet(`${targetCollectionName}.count`, stateSlice);
	const pagination = stateSlice[targetCollectionName].pagination;

	// Derived from the id actually on screen, NOT tracked in state. The URL changes by more than
	// these two buttons — browser Back/Forward, the "Sub Asset of {parent}" link, any in-page link
	// to a sibling entity — and none of those go through `navigate`. A stored index therefore
	// desynced from `activeId`: the "12 of 250" counter kept counting the presses rather than
	// naming the record on screen, and the next arrow press moved relative to an entity the user
	// had already navigated away from. Deriving it means the position can never disagree with what
	// is being displayed.
	const currentIndex = useMemo(() => {
		const mappedIndex = Object.keys(idMap).find((key) => String(idMap[key]) === String(activeId));
		if (mappedIndex !== undefined) {
			return Number(mappedIndex);
		}
		// `activeId` is not in any page loaded so far (e.g. the user followed a link to an entity
		// outside the list). Fall back to its position in the collection's current page.
		return pagination.pageSize * (pagination.current - 1) + indexInCurrentSlice;
	}, [idMap, activeId, pagination, indexInCurrentSlice]);

	const navigate = useCallback(
		(delta) => {
			const index = currentIndex + delta;
			const pagination = stateSlice[targetCollection].pagination;
			const fetching = stateSlice[targetCollection].fetching;
			const pageSize = pagination.pageSize;
			// The 1-based page holding `index` — the page `updatePagination` has to load to reach it.
			const pageForIndex = Math.ceil((index + 1) / pageSize);

			const nextId = idMap[index];
			if (nextId) {
				const url = getEnitityUrl(nextId);
				const fullUrl = `${url}${location.search}`;
				history.push(fullUrl);
			} else {
				setLoading(true);
				updatePagination({ current: pageForIndex, pageSize }, targetCollection).then((data) => {
					setLoading(false);
					if (!data || !data.length) {
						message.info('Reached the end of list!');
						return;
					}
					const loadedIdMap = recordstoNumberedMap(idMap, {
						recordsIds: data.map((item) => item.id),
						pagination: { current: pageForIndex, pageSize },
					});
					setIdMap(loadedIdMap);
					// The record we want is the one AT `index`, which is only `data[0]` when the page
					// boundary is crossed going FORWARD. Going backwards, `index` is the LAST record of
					// the page just loaded (index 19 of a 10-per-page list), so pushing `data[0]` skipped
					// the whole page and landed on its first record — Previous jumped back 10 entities.
					const targetId = loadedIdMap[index];
					if (!targetId) {
						message.info('Reached the end of list!');
						return;
					}
					const url = getEnitityUrl(targetId);
					const fullUrl = `${url}${location.search}`;
					history.push(fullUrl);
				});
			}
			if (nextId && index > 2 && index + 2 < total && !idMap[index + delta * 2] && !fetching) {
				const pageToPrefetch = pageForIndex + delta;
				// There is nothing before page 1, and `current: 0` sends a negative offset.
				if (pageToPrefetch >= 1) {
					updatePagination({ current: pageToPrefetch, pageSize }, targetCollection).then((data) => {
						setIdMap(
							recordstoNumberedMap(idMap, {
								recordsIds: (data || []).map((item) => item.id),
								pagination: { current: pageToPrefetch, pageSize },
							})
						);
						setLoading(false);
					});
				}
			}
		},
		[currentIndex, idMap, total, updatePagination, location, history, stateSlice, getEnitityUrl]
	);

	const onNext = useCallback(() => {
		navigate(1);
	}, [navigate]);

	const onPrevious = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const atStart = currentIndex <= 0;
	const atEnd = total && currentIndex + 1 >= total;

	const handleArrowLeft = useCallback(() => {
		if (!atStart && !loading) {
			onPrevious();
		}
	}, [atStart, loading, onPrevious]);

	const handleArrowRight = useCallback(() => {
		if (!atEnd && !loading) {
			onNext();
		}
	}, [atEnd, loading, onNext]);

	useArrowKeyNavigation({
		onArrowLeft: handleArrowLeft,
		onArrowRight: handleArrowRight,
		enabled: true,
	});

	useEffect(() => {
		// copy targetCollectionName to different targetcollectionName
		if (stateSlice[targetCollectionName] && stateSlice[targetCollectionName].recordsIds.length) {
			cloneTargetCollection && cloneTargetCollection(targetCollectionName, targetCollection);
		}
		return () => {
			// cloneTargetCollection && cloneTargetCollection(targetCollection, targetCollectionName);
		};
	}, []);
	return (
		<div className={cn('mb-2 mr-4 flex flex-row items-center text-gray-300', wrapperclassName)}>
			{currentIndex > -1 ? <span className=" text-white">{currentIndex + 1} </span> : null}
			{total && <span className="ml-1 mr-2 inline-block "> of {total}</span>}

			<Tooltip title={`Previous ${entityName}`}>
				<Button
					className="nextPreviousButton ml-2 mr-2"
					icon={<LeftOutlined />}
					onClick={onPrevious}
					disabled={atStart}
					loading={loading}
					ghost
					size="small"
				/>
			</Tooltip>
			<Tooltip title={`Next ${entityName}`}>
				<Button
					className="nextPreviousButton"
					loading={loading}
					icon={<RightOutlined />}
					onClick={onNext}
					ghost
					disabled={atEnd}
					size="small"
				/>
			</Tooltip>
		</div>
	);
}

function NextPrevious(props) {
	const { stateSlice, targetCollectionName, activeId } = props;
	if (
		!targetCollectionName ||
		!stateSlice[targetCollectionName] ||
		!stateSlice[targetCollectionName].recordsIds.length ||
		stateSlice[targetCollectionName].recordsIds.length < 2 ||
		!activeId
	) {
		return null;
	}

	return <NextPreviousButton key={targetCollectionName} {...props} />;
}

export default React.memo(NextPrevious);
